import type { RawData } from '@orama/orama';

import { oramaIndexVersion } from './orama-schema';

export const SEARCH_INDEX_DB_NAME = 'documint-search-index';
const STORE_NAME = 'orama-data';
// v3 = the stored value is Orama's export object with its term-frequency objects encoded as Maps
// (see encodeIndexData); v2 keys held the raw export object and v1 keys a JSON string, so the
// version marker sends both to pruneStaleIndexKeys.
//
// Keys are scoped to the user because the logout deletion of this database has escape paths
// (a non-leader tab's leadership timeout, broadcast-follower tabs, a blocked delete): a blob
// left behind by another account must be structurally unrestorable rather than merely
// unlikely, or it would restore into the next account's search results.
export const activeIndexKeyFor = (userId: string) => `orama-index-v3-${oramaIndexVersion}-u:${userId}`;
export const archivedIndexKeyFor = (userId: string) => `orama-index-archived-v3-${oramaIndexVersion}-u:${userId}`;
export const allIndexKeysFor = (userId: string): string[] => [activeIndexKeyFor(userId), archivedIndexKeyFor(userId)];

let _db: IDBDatabase | null = null;

export const _resetDBCache = () => {
  _db = null;
};

const getDB = (): Promise<IDBDatabase> => {
  if (_db) return Promise.resolve(_db);
  return openIndexedDB().then((db) => {
    _db = db;
    db.onclose = () => {
      _db = null;
    };
    db.onversionchange = () => {
      db.close();
      _db = null;
    };
    return db;
  });
};

export const openIndexedDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(SEARCH_INDEX_DB_NAME);

    // The DOMException carries the diagnosis in its `name` (VersionError, QuotaExceededError,
    // …); replacing it with a generic Error erases that everywhere it is reported.
    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open IndexedDB'));
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };
  });
};

// Takes a producer rather than the data itself: an Orama export shares live references with
// the running index, and only put()'s clone detaches it. The export must therefore be
// produced here, with no await between it and put() — the DB open above is a
// real async gap whenever the connection was dropped, and a worker message handled in that
// gap would mutate the index mid-snapshot, persisting a torn blob that still loads cleanly.
// The producer may decline by returning null (the caller's state changed during that gap, e.g.
// a rebuild started): nothing is written.
export const storeIndexData = async (produceIndexData: () => RawData | null, indexKey: string): Promise<void> => {
  const db = await getDB();

  return new Promise((resolve, reject) => {
    // Produced (and encoded) before the transaction so a large save() does not hold the
    // readwrite lock, but still in the same synchronous task as the put below. Inside the
    // executor, an encode failure rejects the write like any other persist failure.
    const produced = produceIndexData();
    if (produced === null) {
      resolve();
      return;
    }
    const data = encodeIndexData(produced);

    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const request = store.put(data, indexKey);

    // Settled by the TRANSACTION, not the request: a put reports success well before the
    // transaction commits, and Chrome enforces the storage quota at commit — resolving on the
    // request would report a durable write for data that was then thrown away.
    const failure = () => transaction.error ?? request.error ?? new Error('Failed to store index data');

    request.onerror = () => {
      reject(failure());
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(failure());
    transaction.onabort = () => reject(failure());
  });
};

export const deleteIndexData = async (indexKey: string): Promise<void> => {
  const db = await getDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const request = store.delete(indexKey);

    // Settled by the transaction for the same reason as storeIndexData above: the request
    // succeeding is not the key being gone.
    const failure = () => transaction.error ?? request.error ?? new Error('Failed to delete index data');

    request.onerror = () => {
      reject(failure());
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(failure());
    transaction.onabort = () => reject(failure());
  });
};

// reportPresence answers whether an index is stored as soon as a count in the same readonly
// transaction does, well before a large read settles. One transaction is what makes the answer
// trustworthy: another tab's delete or write cannot land between the count and the read, so
// whoever acts on the answer gets exactly the index the read returns.
export const retrieveIndexData = async (
  indexKey: string,
  reportPresence: (present: boolean) => void
): Promise<RawData | undefined> => {
  const stored = await retrieveStoredValue(indexKey, reportPresence);
  // Decoded here, after the read settled, never inside the request callback: a throw there
  // would leave the promise pending forever instead of rejecting into the worker's
  // restore-failed path.
  return stored === undefined ? undefined : decodeIndexData(stored);
};

const retrieveStoredValue = async (indexKey: string, reportPresence: (present: boolean) => void): Promise<unknown> => {
  const db = await getDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    const countRequest = store.count(indexKey);
    countRequest.onsuccess = (event) => {
      reportPresence((event.target as IDBRequest<number>).result > 0);
    };

    const request = store.get(indexKey);

    request.onsuccess = (event) => {
      resolve((event.target as IDBRequest).result);
    };

    request.onerror = () => {
      reject(request.error ?? new Error('Failed to retrieve index data'));
    };
  });
};

// Deletes every stored index except the given ones. An empty list therefore wipes the store:
// signed out means no user's search data stays on disk.
export const pruneStaleIndexKeys = async (validKeys: IDBValidKey[]): Promise<void> => {
  const db = await getDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const request = store.getAllKeys();

    request.onsuccess = (event) => {
      const keys = (event.target as IDBRequest<IDBValidKey[]>).result;
      keys.forEach((key) => {
        if (!validKeys.includes(key)) {
          store.delete(key);
        }
      });
    };

    request.onerror = () => {
      reject(new Error('Failed to prune stale index keys'));
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(new Error('Failed to prune stale index keys'));
    transaction.onabort = () => reject(new Error('Prune transaction aborted'));
  });
};

// Stored form of an Orama export (DM-1701). Reading the raw export back from IndexedDB makes
// V8 rebuild every object through its structured-clone reader, which falls off a performance
// cliff on the per-doc term-frequency objects (index.frequencies: property → doc → token → count)
// of notes with large vocabularies: 33 s for 8k notes instead of 2 s. Those objects are stored as
// Maps, which clone without that cost, and decodeIndexData rebuilds them with plain assignment, the
// same way Orama builds them while indexing. The rest of the export is stored as is: encoding it
// too was measured slower to save and to restore.
type IndexExport = RawData & { index: { frequencies?: unknown } };

const encodeIndexData = (data: RawData): unknown => {
  const { index } = data as IndexExport;
  if (!index?.frequencies) {
    throw new Error('Search index export has no term frequencies to encode');
  }
  return { ...data, index: { ...index, frequencies: encodeValue(index.frequencies) } };
};

const encodeValue = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  const prototype = Object.getPrototypeOf(value);
  // Anything but a plain object would come back as one and silently lose its contents.
  if (prototype !== Object.prototype && prototype !== null) {
    throw new Error(`Cannot encode ${value.constructor?.name ?? 'object'} in the search index export`);
  }

  const encoded = new Map<string, unknown>();
  for (const key of Object.keys(value)) {
    encoded.set(key, encodeValue((value as Record<string, unknown>)[key]));
  }
  return encoded;
};

const decodeIndexData = (stored: unknown): RawData => {
  const index = (stored as IndexExport | null)?.index;
  if (!(index?.frequencies instanceof Map)) {
    throw new Error('Stored search index is not in the expected format');
  }
  return { ...(stored as RawData), index: { ...index, frequencies: decodeValue(index.frequencies) } };
};

// undefined passes through untouched: Orama leaves it in place of a removed doc's term
// frequencies, and load() expects it back.
const decodeValue = (value: unknown): unknown => {
  if (!(value instanceof Map)) {
    return value;
  }

  const decoded: Record<string, unknown> = {};
  for (const [key, entry] of value) {
    decoded[key] = decodeValue(entry);
  }
  return decoded;
};
